// Record Receipt (45138): the body of the shell side drawer for ONE purchase order. Replaces the old inline ReceiptPanel; every POST is the same
// (one /goods-receipts per line, the preview:true dry run, close_short / close_without_receipt) and the server stays the guard. The form pre-checks
// over-receipt and words soft notes; it never blocks on a date. Host: shell/ReceiptCreateForm.jsx (picker, head, draft pill).
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { IcoLn } from '../../../shell/ActionIcons';
import { Emo } from '../../../shell/EmojiIcons';
import { DrawerActions } from '../../../shell/DrawerBody';
import { formatDate, formatQty, formatMoney } from '../../../utils/format';
import { todayLocal } from '../../Sales/dispatchEta.mjs';
import { LineCard, PreviewBlock, DoneBody } from './receiptBits.jsx';
import { receiptKeySet, postKeyedReceipt, receiptCodeKey } from './receiptKeys.mjs';   // GES-434 idempotency keys
import { receivableLines, planReceipt, closeKind, receiveBody, closeBody, previewBody, closePreviewBody, lineIssue, anyPriceDiff, needsFunding, dateNotes, railOf, costOf, num } from './receiptModel.mjs';
import './receipt.css';
import '../../Sales/dispatch.css';
import { AccountPick } from '../../../components/FormPickers';
import { TextInput } from '../../../components/inputs/Input';

const fresh = () => ({ qtys: {}, unitCosts: {}, storageIds: {}, closeLines: new Set() });
const seedStorages = lines => Object.fromEntries(lines.filter(l => l.storage_id).map(l => [l.id, l.storage_id]));
const asList = (r, k) => (Array.isArray(r) ? r : (r?.[k] || []));

export default function RecordReceiptPanel({ operationId, scopeLineId = null, onRail = () => {}, onDirty = () => {}, onPosted = () => {}, onClose = () => {} }) {
  const { t } = useTranslation('receipt');
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);        // GET /goods-receipts/receivable-orders/:op
  const [loadErr, setLoadErr] = useState('');
  const [st, setStRaw] = useState(fresh);
  const [receiptDate, setReceiptDate] = useState(todayLocal());
  const [shortClose, setShortClose] = useState(false);
  const [notes, setNotes] = useState(''), [reason, setReason] = useState('');
  const [fundingAccount, setFundingAccount] = useState(''), [fundingRequired, setFundingRequired] = useState(false);
  const [storages, setStorages] = useState([]), [accounts, setAccounts] = useState([]);
  const [err, setErr] = useState(null), [busy, setBusy] = useState(false), [previewing, setPreviewing] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [done, setDone] = useState(null), [more, setMore] = useState(null);
  const lock = useRef(false);
  const keys = useRef(null); if (!keys.current) keys.current = receiptKeySet();   // GES-434: one key per line per submit attempt set
  const setSt = f => { setStRaw(f); onDirty(); setPreviewRows(null); };

  const load = () => api.getReceivableOrder(operationId).then(d => { setDetail(d); return d; });
  useEffect(() => {
    let alive = true;
    load().then(d => { if (alive) { setStRaw(s => ({ ...s, storageIds: seedStorages(d.lines) })); onRail(railOf(d.lines, d.receivable)); } }).catch(e => alive && setLoadErr(e.message || t('picker.loadErr')));
    api.getStorages().then(r => alive && setStorages(asList(r, 'storages'))).catch(() => {});
    api.getAccounts().then(r => alive && setAccounts(asList(r, 'accounts'))).catch(() => {});
    return () => { alive = false; };
  }, [operationId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (loadErr) return <div className="errbox" role="alert" data-testid="rcp-load-error">{loadErr}</div>;
  if (!detail) return <div className="hint">{t('loading')}</div>;

  const lines = receivableLines(detail.lines, scopeLineId);
  const isCredit = detail.is_credit === true;
  const ctx = { receiptDate, unitCosts: st.unitCosts, storageIds: st.storageIds, fundingAccount, notes, reason };
  const plan = planReceipt({ lines, qtys: st.qtys, closeLines: st.closeLines, shortClose });
  const issues = lines.filter(l => !st.closeLines.has(l.id) && lineIssue(l, st.qtys[l.id]));
  const tieBlocked = (previewRows || []).some(r => r.tie_out && r.tie_out.block);
  const blocked = busy || previewing || lines.length === 0 || plan.empty || issues.length > 0 || tieBlocked;
  const touched = plan.normal.map(x => x.line);
  const notesSoft = dateNotes({ receiptDate, orderDate: detail.order_date, today: todayLocal(), lines: touched });
  const showFunding = needsFunding({ isCredit, anyDiff: anyPriceDiff(lines, st.unitCosts, st.closeLines), fundingRequired });
  const value = plan.normal.reduce((s, { line, qty }) => s + qty * costOf(line, st.unitCosts), 0);
  const remainderQty = plan.normal.reduce((s, { line, qty }) => s + Math.max(0, num(line.qty_ordered) - num(line.qty_received) - qty), 0);
  const etas = [...new Set(lines.map(l => l.expected_receipt_date).filter(Boolean))].sort();
  const mark = fn => e => { fn(e.target.value); onDirty(); setPreviewRows(null); };

  async function submit() {
    if (lock.current) return;
    lock.current = true; setBusy(true); setErr(null);
    try {
      const echoes = [];
      for (const { line, qty } of plan.normal) {
        const body = receiveBody(line, qty, ctx);
        const resp = await postKeyedReceipt(b => api.requestRaw('/goods-receipts', { method: 'POST', body: JSON.stringify(b) }),
          { ...body, idempotency_key: keys.current.keyFor(line.id, body) });
        if (!resp.ok) {
          if (resp.status === 422 && resp.data?.code === 'RECEIPT_PRICE_FUNDING') { setFundingRequired(true); setErr({ text: t('fundingRequired'), code: resp.data.code }); return; }
          const k = receiptCodeKey(resp.data?.code);
          throw Object.assign(new Error(k ? t(k) : (resp.data?.error || t('err.generic'))), { code: resp.data?.code });
        }
        keys.current.commit(line.id);   // a retry after a later line fails replays this one, never re-posts it
        if (resp.data?.echo) echoes.push({ ...resp.data.echo, item_name: line.item_name });
      }
      for (const line of plan.close) await api.request('/goods-receipts', { method: 'POST', body: JSON.stringify(closeBody(line, closeKind(line), ctx)) });
      for (const { line, qty } of plan.remainder) await api.request('/goods-receipts', { method: 'POST', body: JSON.stringify(closeBody(line, closeKind(line, qty), ctx)) });
      keys.current.clear();   // the next submit is a new receipt
      setPreviewRows(null);
      setDone({ received: plan.normal, closed: [...plan.close.map(line => ({ line, short: num(line.qty_received) > 0.0001 })), ...plan.remainder.map(({ line, qty }) => ({ line, short: num(line.qty_received) + qty > 0.0001 }))], echoes });
      onDirty(false); onPosted(); setMore(null);
      load().then(d => { setMore(d.receivable); onRail(d.receivable ? 'partial' : 'received'); }).catch(() => setMore(false));
    } catch (e) { setErr({ text: e.message || t('err.generic'), code: e.code }); } finally { lock.current = false; setBusy(false); }
  }

  async function preview() {
    setPreviewing(true); setErr(null);
    try {
      const rows = [];
      for (const { line, qty } of plan.normal) {
        const pv = await api.request('/goods-receipts', { method: 'POST', body: JSON.stringify(previewBody(line, qty, ctx)) });
        rows.push({ line, qty, projection: pv?.projection || null, tie_out: pv?.tie_out || null });
      }
      for (const line of plan.close) {
        const cp = await api.request('/goods-receipts', { method: 'POST', body: JSON.stringify(closePreviewBody(line, ctx)) });
        rows.push({ line, close: cp || null, tie_out: cp?.tie_out || null });
      }
      setPreviewRows(rows);
    } catch (e) { setErr({ text: e.message || t('err.previewFailed') }); } finally { setPreviewing(false); }
  }

  const another = () => {
    setDone(null); setErr(null); setPreviewRows(null); setMore(null); setShortClose(false); setNotes(''); setReason(''); setFundingAccount(''); setFundingRequired(false);
    setReceiptDate(todayLocal()); setStRaw({ ...fresh(), storageIds: seedStorages(detail.lines) }); onRail(railOf(detail.lines, detail.receivable));
  };
  const openOrder = () => { onClose(); navigate(`/purchasing/purchase-orders/orders/${encodeURIComponent(operationId)}`); };
  const closeBtn = <button type="button" className="btn" onClick={onClose}>{t('close')}</button>;

  if (done) {
    return (
      <>
        <DoneBody done={done} more={more} />
        <DrawerActions legacyRow="act-row" legacyClose={closeBtn}>
          {() => (<>
            <button type="button" className="btn" data-testid="rcp-record-another" disabled={more !== true} title={more === false ? t('done.nothingLeft') : undefined} onClick={another}>{t('done.another')}</button>
            <button type="button" className="btn success" data-testid="rcp-open-order" onClick={openOrder}><IcoLn name="order" />{t('done.openOrder')}</button>
          </>)}
        </DrawerActions>
      </>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <dl className="dsp-facts" data-testid="rcp-facts">
        <dt>{t('field.supplier')}</dt><dd>{detail.partner_name || '—'}</dd>
        <dt>{t('field.orderDate')}</dt><dd>{detail.order_date ? formatDate(detail.order_date) : '—'}</dd>
        <dt>{t('field.payment')}</dt><dd>{t(isCredit ? 'payment.credit' : 'payment.cash')}</dd>
        <dt>{t('field.lineEta')}</dt><dd>{etas.length === 0 ? '—' : etas.length === 1 ? formatDate(etas[0]) : t('etaVaries')}</dd>
      </dl>
      {lines.length === 0 ? <div className="hint" data-testid="rcp-nothing"><Emo name="check" /> {t('picker.nothingLeft')}</div> : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="sec-lab" style={{ margin: 0 }}>{t('section.lines')}</div>
          {lines.map(l => <LineCard key={l.id} line={l} st={st} setSt={setSt} storages={storages} isCredit={isCredit} />)}
          <div className="rcp-total" data-testid="rcp-total"><span>{t('total.label')}</span><span>{plan.normal.length ? formatMoney(Math.round(value)) : t('total.none')}</span></div>
          <div className="rcp-note">{t('hint.cost')}</div>
        </div>)}
      {lines.length > 0 && (
        <div className="form">
          <div className="field"><label htmlFor="rcp-date">{t('field.receiptDate')}</label>
            <TextInput id="rcp-date" type="date" value={receiptDate} data-testid="rcp-date" onChange={mark(setReceiptDate)} /><span className="sub">{t('hint.receiptDate')}</span></div>
          <div className="field full"><label className="chk"><input type="checkbox" checked={shortClose} data-testid="rcp-shortclose" onChange={e => { setShortClose(e.target.checked); onDirty(); setPreviewRows(null); }} /> {t('shortClose')}</label></div>
          {shortClose && remainderQty > 0 && <div className="field full"><div className="warnbox" role="status"><Emo name="warn" /> <div>{t('shortCloseNote', { qty: formatQty(remainderQty) })}</div></div></div>}
          {showFunding && (
            <div className="field full"><label htmlFor="rcp-fund">{t('field.funding')}<span className="req">*</span></label>
              <AccountPick id="rcp-fund" testid="rcp-funding" accounts={accounts.filter(a => a.type === 'cash' || a.type === 'bank')} value={fundingAccount} onChange={v => mark(setFundingAccount)({ target: { value: v } })} required placeholder={t('fundingPick')} /><span className="sub">{t('fundingHint')}</span></div>)}
          <div className="field full"><label htmlFor="rcp-notes">{t('field.notes')}</label><textarea id="rcp-notes" rows={2} value={notes} placeholder={t('ph.notes')} onChange={mark(setNotes)} /></div>
          <div className="field full"><label htmlFor="rcp-reason">{t('field.reason')}</label><TextInput id="rcp-reason" type="text" value={reason} placeholder={t('ph.reason')} onChange={mark(setReason)} /></div>
        </div>)}
      {notesSoft.map((n, i) => <div className="warnbox" key={i} role="status" data-testid="rcp-soft-warn"><Emo name="warn" /> <div>
        {n.kind === 'future' ? t('warn.future') : n.kind === 'beforeOrder' ? t('warn.beforeOrder', { date: formatDate(n.date) })
          : t(n.kind === 'lineLate' ? 'warn.lineLate' : 'warn.afterEta', { item: n.item, date: formatDate(n.date), count: n.days })}</div></div>)}
      {previewRows && <PreviewBlock rows={previewRows} />}
      {tieBlocked && <div className="ferr" role="alert"><Emo name="stop" /> {t('tie.blocked')}</div>}
      {lines.length > 0 && <div className="hint"><Emo name="lock" /> {t('hint.post')}</div>}
      {err && <div className="ferr" role="alert" data-testid="rcp-error"><Emo name="stop" /> {err.text}{err.code && <> <code>{err.code}</code></>}</div>}
      <DrawerActions legacyRow="act-row" legacyClose={closeBtn}>
        {() => (<>
          {previewRows && <button type="button" className="btn" disabled={busy} onClick={() => setPreviewRows(null)}>{t('btn.clearPreview')}</button>}
          <button type="button" className="btn" data-testid="rcp-preview-btn" disabled={previewing || busy || lines.length === 0 || plan.empty || issues.length > 0} onClick={preview}>{previewing ? t('btn.previewing') : t('btn.preview')}</button>
          <button type="button" className="btn success" data-testid="rcp-record" disabled={blocked} onClick={submit}>{busy ? t('working') : t('btn.record')}</button>
        </>)}
      </DrawerActions>
    </div>
  );
}
